import React, { useState, useMemo, useRef } from 'react';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Combobox } from '../../components/ui/combobox';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../components/ui/collapsible';
import { ChevronDown, Paperclip, X, Upload, FileText } from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

const API = '/api';

const EXPENSE_TYPES = [
  'Payroll & Benefits', 'SaaS & Tools', 'Professional Services', 'Marketing & Advertising',
  'Office & Facilities', 'Travel & Entertainment', 'Hosting & Infrastructure',
  'Insurance & Compliance', 'R&D', 'Bank Fees', 'Depreciation', 'Other',
];
const RECEIPT_TYPES = ['Invoice', 'Receipt', 'Contract', 'Bank Statement', 'Email Confirmation', 'Screenshot', 'Other', 'None'];
const ORG_FUNCTIONS = [
  'Engineering', 'Product', 'Design', 'Marketing', 'Sales', 'Operations',
  'Finance', 'Legal', 'HR', 'Customer Support', 'Executive', 'Other',
];
const CURRENCIES = ['USD', 'GBP', 'EUR', 'AUD', 'CAD', 'INR', 'SGD', 'AED', 'CHF', 'JPY'];
const INVOICE_STATUSES = ['Pending', 'Invoiced', 'Paid', 'Refunded'];
const PAYMENT_CHANNELS = ['Bank Transfer', 'Credit Card', 'PayPal', 'Stripe', 'Wire Transfer', 'Cash', 'Crypto', 'Check', 'Other'];

const SectionHeader = ({ label }) => (
  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider col-span-2">{label}</p>
);

const FieldLabel = ({ children, required }) => (
  <label className="text-xs text-muted-foreground mb-1 block">
    {children}{required && <span className="text-red-400 ml-0.5">*</span>}
  </label>
);

// File Upload Widget
const FileUploadWidget = ({ file, fileUrl, onFileSelect, onClear, label = "Attach Receipt/Evidence" }) => {
  const inputRef = useRef(null);
  return (
    <div className="col-span-2">
      <FieldLabel>{label}</FieldLabel>
      <div className="flex items-center gap-2">
        {file ? (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-muted/50 rounded-md border text-xs flex-1">
            <Paperclip className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
            <span className="truncate font-medium">{file.name}</span>
            <span className="text-muted-foreground shrink-0">({(file.size / 1024).toFixed(0)} KB)</span>
            <button type="button" onClick={onClear} className="ml-auto text-muted-foreground hover:text-foreground">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : fileUrl ? (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 dark:bg-emerald-950/30 rounded-md border border-emerald-200 dark:border-emerald-800 text-xs flex-1">
            <FileText className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
            <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="truncate text-emerald-700 dark:text-emerald-400 underline">View uploaded file</a>
            <button type="button" onClick={onClear} className="ml-auto text-muted-foreground hover:text-foreground">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="text-xs"
            onClick={() => inputRef.current?.click()}
            data-testid="file-upload-btn"
          >
            <Upload className="w-3.5 h-3.5 mr-1.5" />
            Choose File
          </Button>
        )}
        <input ref={inputRef} type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.csv,.xls,.xlsx"
          onChange={e => { if (e.target.files?.[0]) onFileSelect(e.target.files[0]); e.target.value = ''; }} />
      </div>
    </div>
  );
};

// Collapsible section for grouping fields
const CollapsibleSection = ({ label, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Collapsible open={open} onOpenChange={setOpen} className="col-span-2">
      <CollapsibleTrigger className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors w-full py-1">
        <ChevronDown className={cn("w-3.5 h-3.5 transition-transform", open && "rotate-180")} />
        {label}
        {!open && <span className="text-[10px] text-muted-foreground/60 ml-auto">Click to expand</span>}
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-2">
        <div className="grid grid-cols-2 gap-3">
          {children}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

// ==================== EXPENSE MODAL CONTENT ====================
export const ExpenseModalContent = ({
  expenseForm, setExpenseForm, editItem,
  refAccounts, refBusinessUnits, refVendors, refProjects, refCategories,
  orgDepartments, allStaffAndContractors, activeWorkspace,
}) => {
  const [receiptFile, setReceiptFile] = useState(null);

  // Build cascading category hierarchy — all wrapped in useMemo
  const allExpCats = useMemo(() => refCategories || [], [refCategories]);
  const expTypes = useMemo(() => allExpCats.filter(c => !c.parent_code), [allExpCats]);

  const typeOptions = useMemo(() =>
    expTypes.length > 0
      ? expTypes
      : EXPENSE_TYPES.map(t => ({ name: t, code: '', category_id: t })),
    [expTypes]
  );

  // Build combobox options (deduplicated)
  const vendorOptions = useMemo(() =>
    (refVendors || []).map(v => ({ value: v.name, label: v.name })),
    [refVendors]
  );
  const accountOptions = useMemo(() =>
    (refAccounts || []).map(a => ({ value: a.name, label: a.name })),
    [refAccounts]
  );
  const buOptions = useMemo(() =>
    (refBusinessUnits || []).map(b => ({ value: b.name, label: b.name })),
    [refBusinessUnits]
  );
  const projectOptions = useMemo(() =>
    (refProjects || []).map(p => ({ value: p.name, label: p.name })),
    [refProjects]
  );
  const staffOptions = useMemo(() => {
    const seen = new Set();
    return (allStaffAndContractors || []).filter(m => {
      const key = (m.name || m.email || '').toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    }).map(m => ({
      value: m.name || m.email,
      label: `${m.name || m.email}${m._active === false ? ' (Offboarded)' : ''}`,
    }));
  }, [allStaffAndContractors]);

  const deptOptions = useMemo(() => {
    if (orgDepartments?.length > 0) return orgDepartments.map(d => ({ value: d.name, label: d.name }));
    return ORG_FUNCTIONS.map(f => ({ value: f, label: f }));
  }, [orgDepartments]);

  const typeSelectOptions = useMemo(() =>
    typeOptions.map(t => ({ value: t.name, label: t.code ? `${t.code} — ${t.name}` : t.name })),
    [typeOptions]
  );
  const subTypeSelectOptions = useMemo(() => {
    const subs = expenseForm.expense_type
      ? allExpCats.filter(c => c.parent_code === expenseForm.expense_type)
      : [];
    return subs.map(c => ({ value: c.name, label: c.code ? `${c.code} — ${c.name}` : c.name }));
  }, [allExpCats, expenseForm.expense_type]);

  // Expose upload function via ref for parent to call post-save
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (expenseForm._pendingUpload && receiptFile && activeWorkspace) {
      const txId = expenseForm._pendingUpload;
      setExpenseForm(p => ({ ...p, _pendingUpload: undefined }));
      const formData = new FormData();
      formData.append('file', receiptFile);
      fetch(`${API}/finance/expenses/${activeWorkspace.workspace_id}/${txId}/upload-receipt`, {
        method: 'POST', credentials: 'include', body: formData,
      }).then(res => {
        if (res.ok) toast.success('Receipt uploaded');
        else toast.error('Receipt upload failed');
      }).catch(() => toast.error('Receipt upload error'));
    }
  }, [expenseForm._pendingUpload]);

  return (
    <div className="space-y-4">
      {/* Section 1: Essential Details */}
      <div className="grid grid-cols-2 gap-3">
        <SectionHeader label="Essential Details" />
        <div>
          <FieldLabel required>Date of Expense</FieldLabel>
          <Input type="date" value={expenseForm.date} onChange={e => setExpenseForm(p => ({ ...p, date: e.target.value }))} data-testid="expense-field-date" />
        </div>
        <div>
          <FieldLabel required>Amount (USD)</FieldLabel>
          <Input type="number" step="0.01" value={expenseForm.amount_usd} onChange={e => setExpenseForm(p => ({ ...p, amount_usd: e.target.value }))} placeholder="0.00" data-testid="expense-field-amount" />
        </div>
        <div>
          <FieldLabel>Expense Item</FieldLabel>
          <Input value={expenseForm.expense_item} onChange={e => setExpenseForm(p => ({ ...p, expense_item: e.target.value }))} placeholder="e.g. Cloud Hosting" data-testid="expense-field-item" />
        </div>
        <div>
          <FieldLabel>Vendor/Supplier</FieldLabel>
          <Combobox
            value={expenseForm.vendor || ''}
            onValueChange={v => setExpenseForm(p => ({ ...p, vendor: v }))}
            options={vendorOptions}
            placeholder="Search vendors..."
            searchPlaceholder="Type to search vendors..."
            emptyText="No vendors found. Add in Vendors tab."
            data-testid="expense-field-vendor"
          />
        </div>
        <div className="col-span-2">
          <FieldLabel>Expense Description</FieldLabel>
          <Input value={expenseForm.description} onChange={e => setExpenseForm(p => ({ ...p, description: e.target.value }))} placeholder="e.g. AWS Feb 2026 invoice" data-testid="expense-field-desc" />
        </div>
      </div>

      {/* Section 2: Category (Cascading) */}
      <div className="bg-muted/30 rounded-lg p-3 space-y-3 border">
        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Expense Category</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <FieldLabel>Expense Type</FieldLabel>
            <Combobox
              value={expenseForm.expense_type || ''}
              onValueChange={v => setExpenseForm(p => ({ ...p, expense_type: v, expense_sub_type: '' }))}
              options={typeSelectOptions}
              placeholder="Select type..."
              searchPlaceholder="Search types..."
              data-testid="expense-field-type"
            />
          </div>
          <div>
            <FieldLabel>Expense Sub Type</FieldLabel>
            <Combobox
              value={expenseForm.expense_sub_type || ''}
              onValueChange={v => setExpenseForm(p => ({ ...p, expense_sub_type: v }))}
              options={subTypeSelectOptions}
              placeholder={!expenseForm.expense_type ? 'Select type first' : subTypeSelectOptions.length === 0 ? 'No sub-types' : 'Select sub-type...'}
              disabled={!expenseForm.expense_type || subTypeSelectOptions.length === 0}
              data-testid="expense-field-subtype"
            />
          </div>
        </div>
      </div>

      {/* Section 3: Attribution */}
      <div className="grid grid-cols-2 gap-3">
        <SectionHeader label="Attribution" />
        <div>
          <FieldLabel>Staff / Contractor</FieldLabel>
          <Combobox
            value={expenseForm.staff_contractor_name || ''}
            onValueChange={v => setExpenseForm(p => ({ ...p, staff_contractor_name: v }))}
            options={staffOptions}
            placeholder="Search staff..."
            searchPlaceholder="Type name to search..."
            emptyText="No matching staff found."
            data-testid="expense-field-staff-name"
          />
        </div>
        <div>
          <FieldLabel>Account Paid From</FieldLabel>
          <Combobox
            value={expenseForm.account_paid_from || ''}
            onValueChange={v => setExpenseForm(p => ({ ...p, account_paid_from: v }))}
            options={accountOptions}
            placeholder="Search accounts..."
            searchPlaceholder="Type to search..."
            emptyText="No accounts. Add in Accounts tab."
            data-testid="expense-field-account"
          />
        </div>
        <div>
          <FieldLabel>Business Unit</FieldLabel>
          <Combobox
            value={expenseForm.business_unit || ''}
            onValueChange={v => setExpenseForm(p => ({ ...p, business_unit: v }))}
            options={buOptions}
            placeholder="Search units..."
            emptyText="No units. Add in Business Units tab."
            data-testid="expense-field-bu"
          />
        </div>
        <div>
          <FieldLabel>Organisation Function</FieldLabel>
          <Combobox
            value={expenseForm.org_function || ''}
            onValueChange={v => setExpenseForm(p => ({ ...p, org_function: v }))}
            options={deptOptions}
            placeholder="Select department..."
            searchPlaceholder="Type to search..."
            data-testid="expense-field-org-fn"
          />
        </div>
        <div>
          <FieldLabel>Project Attribution</FieldLabel>
          <Combobox
            value={expenseForm.project_attribution || ''}
            onValueChange={v => setExpenseForm(p => ({ ...p, project_attribution: v }))}
            options={projectOptions}
            placeholder="Search projects..."
            emptyText="No projects. Add in Projects tab."
            data-testid="expense-field-project"
          />
        </div>
      </div>

      {/* Section 4: Evidence & Receipt */}
      <div className="grid grid-cols-2 gap-3">
        <SectionHeader label="Evidence & Receipt" />
        <div>
          <FieldLabel>Receipt/Evidence Type</FieldLabel>
          <Select value={expenseForm.receipt_type || ''} onValueChange={v => setExpenseForm(p => ({ ...p, receipt_type: v }))}>
            <SelectTrigger data-testid="expense-field-receipt-type"><SelectValue placeholder="Select type" /></SelectTrigger>
            <SelectContent>
              {RECEIPT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <FieldLabel>Receipt/Evidence Link</FieldLabel>
          <Input value={expenseForm.receipt_link} onChange={e => setExpenseForm(p => ({ ...p, receipt_link: e.target.value }))} placeholder="URL or file reference" data-testid="expense-field-receipt-link" />
        </div>
        <FileUploadWidget
          file={receiptFile}
          fileUrl={editItem?.receipt_file_url}
          onFileSelect={setReceiptFile}
          onClear={() => setReceiptFile(null)}
          label="Upload Receipt/Evidence"
        />
      </div>

      {/* Section 5: Currency & Banking (collapsible) */}
      <CollapsibleSection label="Currency & Banking Details">
        <div>
          <FieldLabel>Original Currency Amount</FieldLabel>
          <Input type="number" step="0.01" value={expenseForm.original_amount} onChange={e => setExpenseForm(p => ({ ...p, original_amount: e.target.value }))} placeholder="0.00" data-testid="expense-field-orig-amount" />
        </div>
        <div>
          <FieldLabel>Original Currency Type</FieldLabel>
          <Select value={expenseForm.original_currency} onValueChange={v => setExpenseForm(p => ({ ...p, original_currency: v }))}>
            <SelectTrigger data-testid="expense-field-orig-currency"><SelectValue /></SelectTrigger>
            <SelectContent>{CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <FieldLabel>Bank Statement Entry</FieldLabel>
          <Input value={expenseForm.bank_statement_entry} onChange={e => setExpenseForm(p => ({ ...p, bank_statement_entry: e.target.value }))} placeholder="Statement reference" data-testid="expense-field-bank-entry" />
        </div>
        <div>
          <FieldLabel>Bank Statement Date</FieldLabel>
          <Input type="date" value={expenseForm.bank_statement_date} onChange={e => setExpenseForm(p => ({ ...p, bank_statement_date: e.target.value }))} data-testid="expense-field-bank-date" />
        </div>
      </CollapsibleSection>

      {/* Section 6: Comments */}
      <div>
        <FieldLabel>Comments</FieldLabel>
        <Textarea value={expenseForm.comments} onChange={e => setExpenseForm(p => ({ ...p, comments: e.target.value }))} rows={2} data-testid="expense-field-comments" />
      </div>
    </div>
  );
};

// ==================== INCOME MODAL CONTENT ====================
export const IncomeModalContent = ({
  incomeForm, setIncomeForm, editItem,
  refAccounts, refBusinessUnits, refSourceOfFunds, refIncomeCategories,
  allStaffAndContractors, activeWorkspace, fetchRefData,
}) => {
  const [invoiceFile, setInvoiceFile] = useState(null);

  // Cascading hierarchy — wrapped in useMemo
  const allCats = useMemo(() => refIncomeCategories || [], [refIncomeCategories]);
  const incTypes = useMemo(() => allCats.filter(c => !c.parent_code), [allCats]);

  // Combobox options
  const sourceOptions = useMemo(() =>
    (refSourceOfFunds || []).map(s => ({
      value: s.name,
      label: `${s.name}${s.is_investor ? ' (Investor)' : ''}`,
    })),
    [refSourceOfFunds]
  );
  const accountOptions = useMemo(() =>
    (refAccounts || []).map(a => ({ value: a.name, label: a.name })),
    [refAccounts]
  );
  const buOptions = useMemo(() =>
    (refBusinessUnits || []).map(b => ({ value: b.name, label: b.name })),
    [refBusinessUnits]
  );
  const staffOptions = useMemo(() => {
    const seen = new Set();
    return (allStaffAndContractors || []).filter(m => {
      const key = (m.name || m.email || '').toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    }).map(m => ({ value: m.name || m.email, label: m.name || m.email }));
  }, [allStaffAndContractors]);

  const incTypeOptions = useMemo(() =>
    incTypes.map(c => ({ value: c.name, label: c.code ? `${c.code} — ${c.name}` : c.name })),
    [incTypes]
  );
  const classificationOptions = useMemo(() => {
    const items = incomeForm.income_source_type
      ? allCats.filter(c => c.parent_code === incomeForm.income_source_type)
      : [];
    return items.map(c => ({ value: c.name, label: c.code ? `${c.code} — ${c.name}` : c.name }));
  }, [allCats, incomeForm.income_source_type]);
  const marketOptions = useMemo(() => {
    const items = incomeForm.income_classification
      ? allCats.filter(c => c.parent_code === incomeForm.income_classification)
      : [];
    return items.map(c => ({ value: c.name, label: c.name }));
  }, [allCats, incomeForm.income_classification]);

  const handleClassificationChange = (v) => {
    setIncomeForm(p => ({ ...p, income_classification: v, market: '' }));
    // Auto-mark investor if classification contains "investment"
    if (v.toLowerCase().includes('investment') && incomeForm.source_of_funds && activeWorkspace) {
      fetch(`${API}/finance/ref/${activeWorkspace.workspace_id}/source-of-funds/mark-investor`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source_name: incomeForm.source_of_funds, is_investor: true }),
      }).then(() => { toast.info(`"${incomeForm.source_of_funds}" marked as Investor`); if (fetchRefData) fetchRefData(); }).catch(() => {});
    }
  };

  return (
    <div className="space-y-4">
      {/* Section 1: Essential Details */}
      <div className="grid grid-cols-2 gap-3">
        <SectionHeader label="Essential Details" />
        <div>
          <FieldLabel required>Date Income Received</FieldLabel>
          <Input type="date" value={incomeForm.date_received} onChange={e => setIncomeForm(p => ({ ...p, date_received: e.target.value }))} data-testid="income-field-date" />
        </div>
        <div>
          <FieldLabel required>Amount (USD)</FieldLabel>
          <Input type="number" step="0.01" value={incomeForm.amount_usd} onChange={e => setIncomeForm(p => ({ ...p, amount_usd: e.target.value }))} placeholder="0.00" data-testid="income-field-amount" />
        </div>
        <div className="col-span-2">
          <FieldLabel>Income Item Summary</FieldLabel>
          <Input value={incomeForm.income_item_summary} onChange={e => setIncomeForm(p => ({ ...p, income_item_summary: e.target.value }))} placeholder="e.g. SaaS subscription Q1" data-testid="income-field-summary" />
        </div>
        <div>
          <FieldLabel>Source of Funds</FieldLabel>
          <Combobox
            value={incomeForm.source_of_funds || ''}
            onValueChange={v => setIncomeForm(p => ({ ...p, source_of_funds: v }))}
            options={sourceOptions}
            placeholder="Search sources..."
            searchPlaceholder="Type to search..."
            emptyText="No sources. Add in Source of Funds tab."
            data-testid="income-field-source"
          />
        </div>
        <div>
          <FieldLabel>Is Loan?</FieldLabel>
          <div className="flex items-center gap-2 h-9 px-2">
            <Checkbox checked={incomeForm.is_loan === 'Y'} onCheckedChange={v => setIncomeForm(p => ({ ...p, is_loan: v ? 'Y' : 'N' }))} data-testid="income-field-is-loan" />
            <span className="text-xs">{incomeForm.is_loan === 'Y' ? 'Yes — Loan' : 'No'}</span>
          </div>
        </div>
      </div>

      {/* Section 2: Income Category (Cascading) */}
      <div className="bg-muted/30 rounded-lg p-3 space-y-3 border">
        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Income Category</p>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <FieldLabel>Income Type</FieldLabel>
            <Combobox
              value={incomeForm.income_source_type || ''}
              onValueChange={v => setIncomeForm(p => ({ ...p, income_source_type: v, income_classification: '', market: '' }))}
              options={incTypeOptions}
              placeholder="Select type..."
              searchPlaceholder="Search types..."
              emptyText="No types. Add in Income Types tab."
              data-testid="income-field-source-type"
            />
          </div>
          <div>
            <FieldLabel>Classification</FieldLabel>
            <Combobox
              value={incomeForm.income_classification || ''}
              onValueChange={handleClassificationChange}
              options={classificationOptions}
              placeholder={incomeForm.income_source_type ? 'Select...' : 'Select type first'}
              disabled={!incomeForm.income_source_type}
              emptyText="No classifications for this type."
              data-testid="income-field-classification"
            />
          </div>
          <div>
            <FieldLabel>Market</FieldLabel>
            <Combobox
              value={incomeForm.market || ''}
              onValueChange={v => setIncomeForm(p => ({ ...p, market: v }))}
              options={marketOptions}
              placeholder={incomeForm.income_classification ? 'Select...' : 'Select classification first'}
              disabled={!incomeForm.income_classification}
              emptyText="No markets for this classification."
              data-testid="income-field-market"
            />
          </div>
        </div>
      </div>

      {/* Section 3: Invoice Details */}
      <CollapsibleSection label="Invoice Details" defaultOpen={!!(incomeForm.invoice_number || incomeForm.invoice_status)}>
        <div>
          <FieldLabel>Invoice Number</FieldLabel>
          <Input value={incomeForm.invoice_number} onChange={e => setIncomeForm(p => ({ ...p, invoice_number: e.target.value }))} placeholder="e.g. INV-2026-001" data-testid="income-field-inv-num" />
        </div>
        <div>
          <FieldLabel>Invoice Status</FieldLabel>
          <Select value={incomeForm.invoice_status || ''} onValueChange={v => setIncomeForm(p => ({ ...p, invoice_status: v }))}>
            <SelectTrigger data-testid="income-field-inv-status"><SelectValue placeholder="Select status" /></SelectTrigger>
            <SelectContent>
              {INVOICE_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <FieldLabel>Date Invoice Issued</FieldLabel>
          <Input type="date" value={incomeForm.invoice_issue_date} onChange={e => setIncomeForm(p => ({ ...p, invoice_issue_date: e.target.value }))} data-testid="income-field-issue-date" />
        </div>
        <div>
          <FieldLabel>Due Date of Payment</FieldLabel>
          <Input type="date" value={incomeForm.due_date} onChange={e => setIncomeForm(p => ({ ...p, due_date: e.target.value }))} data-testid="income-field-due-date" />
        </div>
        <div>
          <FieldLabel>Days Late</FieldLabel>
          <Input type="number" value={incomeForm.days_late} onChange={e => setIncomeForm(p => ({ ...p, days_late: e.target.value }))} placeholder="Auto-calculated if blank" data-testid="income-field-days-late" />
        </div>
      </CollapsibleSection>

      {/* Section 4: Payment & Attribution */}
      <div className="grid grid-cols-2 gap-3">
        <SectionHeader label="Payment & Attribution" />
        <div>
          <FieldLabel>Paid To (Account)</FieldLabel>
          <Combobox
            value={incomeForm.income_paid_to || ''}
            onValueChange={v => setIncomeForm(p => ({ ...p, income_paid_to: v }))}
            options={accountOptions}
            placeholder="Search accounts..."
            emptyText="No accounts. Add in Accounts tab."
            data-testid="income-field-paid-to"
          />
        </div>
        <div>
          <FieldLabel>Payment Channel</FieldLabel>
          <Select value={incomeForm.payment_channel || ''} onValueChange={v => setIncomeForm(p => ({ ...p, payment_channel: v }))}>
            <SelectTrigger data-testid="income-field-channel"><SelectValue placeholder="Select channel" /></SelectTrigger>
            <SelectContent>
              {PAYMENT_CHANNELS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <FieldLabel>Business Unit</FieldLabel>
          <Combobox
            value={incomeForm.business_unit || ''}
            onValueChange={v => setIncomeForm(p => ({ ...p, business_unit: v }))}
            options={buOptions}
            placeholder="Search units..."
            emptyText="No units. Add in Business Units tab."
            data-testid="income-field-bu"
          />
        </div>
        <div>
          <FieldLabel>Sales Person</FieldLabel>
          <Combobox
            value={incomeForm.sales_person || ''}
            onValueChange={v => setIncomeForm(p => ({ ...p, sales_person: v }))}
            options={staffOptions}
            placeholder="Search staff..."
            data-testid="income-field-sales"
          />
        </div>
      </div>

      {/* Section 5: Evidence & Receipt */}
      <div className="grid grid-cols-2 gap-3">
        <SectionHeader label="Evidence & Receipt" />
        <div>
          <FieldLabel>Receipt Issued?</FieldLabel>
          <Select value={incomeForm.receipt_issued || 'N'} onValueChange={v => setIncomeForm(p => ({ ...p, receipt_issued: v }))}>
            <SelectTrigger data-testid="income-field-receipt-issued"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="N">No</SelectItem>
              <SelectItem value="Y">Yes</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <FieldLabel>Receipt Link</FieldLabel>
          <Input value={incomeForm.receipt_link} onChange={e => setIncomeForm(p => ({ ...p, receipt_link: e.target.value }))} placeholder="URL or file reference" data-testid="income-field-receipt-link" />
        </div>
        <FileUploadWidget
          file={invoiceFile}
          fileUrl={editItem?.invoice_file_url}
          onFileSelect={setInvoiceFile}
          onClear={() => setInvoiceFile(null)}
          label="Upload Invoice/Receipt"
        />
      </div>

      {/* Section 6: Currency (collapsible) */}
      <CollapsibleSection label="Currency Details">
        <div>
          <FieldLabel>Original Currency Amount</FieldLabel>
          <Input type="number" step="0.01" value={incomeForm.original_amount} onChange={e => setIncomeForm(p => ({ ...p, original_amount: e.target.value }))} placeholder="0.00" data-testid="income-field-orig-amount" />
        </div>
        <div>
          <FieldLabel>Original Currency Type</FieldLabel>
          <Select value={incomeForm.original_currency || 'USD'} onValueChange={v => setIncomeForm(p => ({ ...p, original_currency: v }))}>
            <SelectTrigger data-testid="income-field-orig-currency"><SelectValue /></SelectTrigger>
            <SelectContent>{CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </CollapsibleSection>

      {/* Section 7: Note */}
      <div>
        <FieldLabel>Income Note</FieldLabel>
        <Textarea value={incomeForm.income_note} onChange={e => setIncomeForm(p => ({ ...p, income_note: e.target.value }))} rows={2} data-testid="income-field-note" />
      </div>
    </div>
  );
};
